import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { useUnicornStudio, UNICORN_PROJECT_ID } from "../lib/useUnicornStudio";
import { useWaitlist } from "./WaitlistDialog";

/**
 * Interstitial between the 3D story and Services.
 * Uses the UnicornStudio "Vitruvian man" embed (desktop) as a full-bleed backdrop —
 * the human standard every close is measured against.
 */
export const HumanStandard = () => {
  useUnicornStudio();
  const { open } = useWaitlist();
  // If the runtime can't create a WebGL context (software GPUs, headless, old devices),
  // fall back to the stars field so the section never reads as empty.
  const [embedFailed, setEmbedFailed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      const canvas = document.querySelector("#human-standard [data-us-project] canvas");
      if (!canvas) setEmbedFailed(true);
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="human-standard"
      data-testid="human-standard-section"
      className="relative min-h-[70vh] lg:min-h-screen overflow-hidden bg-black border-y border-white/20"
    >
      {/* Vitruvian animation — desktop only */}
      <div className="absolute inset-0 hidden lg:block" aria-hidden="true">
        <div data-us-project={UNICORN_PROJECT_ID} style={{ width: "100%", height: "100%", minHeight: "100vh" }} />
      </div>
      {/* Stars: mobile always; desktop only when the embed can't render */}
      <div className={`absolute inset-0 axt-stars ${embedFailed ? "" : "lg:hidden"}`} aria-hidden="true" data-testid="human-standard-fallback" />
      {embedFailed && (
        <div className="absolute inset-0 hidden lg:flex items-center justify-end pr-[12%] pointer-events-none" aria-hidden="true">
          <div className="relative w-[34vw] max-w-[520px] aspect-square rounded-full border border-white/15">
            <div className="absolute inset-[9%] border border-white/12" />
            <div className="absolute inset-[9%] border border-white/12 rotate-45" />
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10" />
          </div>
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(90deg,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.55)_45%,transparent_75%)]" aria-hidden="true" />

      {/* frame brackets local to the section */}
      <div className="absolute top-0 left-0 w-8 h-8 lg:w-12 lg:h-12 border-t border-l border-white/30" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-8 h-8 lg:w-12 lg:h-12 border-t border-r border-white/30" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-8 h-8 lg:w-12 lg:h-12 border-b border-l border-white/30" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-8 h-8 lg:w-12 lg:h-12 border-b border-r border-white/30" aria-hidden="true" />

      <div className="relative z-10 min-h-[70vh] lg:min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 py-24">
          <div className="max-w-xl">
            <div className="axt-rule mb-5">
              <span>002 · Machine speed, human signature</span>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute -left-4 top-1 bottom-1 w-1 axt-dither opacity-40" aria-hidden="true" />
              <h2
                data-testid="human-standard-headline"
                className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-[color:var(--axt-text)]"
              >
                The close stops being a deadline.
              </h2>
            </div>
            <div className="hidden lg:flex gap-1 mt-5 opacity-40" aria-hidden="true">
              {Array.from({ length: 40 }).map((_, i) => (
                <div key={i} className="w-0.5 h-0.5 bg-white rounded-full" />
              ))}
            </div>
            <p className="mt-6 text-sm sm:text-base leading-relaxed text-[color:var(--axt-text-dim)] max-w-md">
              Capital calls, management fees, waterfalls, and LP reporting — run by AI agents, supervised by senior fund accountants, and
              finished while you&rsquo;re still in the meeting.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="human-standard-cta-button" onClick={() => open("human-standard-see-it-run")} className="axt-btn-primary h-11 px-6">
                See it run
              </Button>
              <Button data-testid="human-standard-services-button" variant="ghost" onClick={() => open("human-standard-talk")} className="axt-btn-ghost h-11 px-6">
                Talk to a fund accountant
              </Button>
            </div>
            <div className="hidden lg:flex items-center gap-2 mt-8 opacity-40" aria-hidden="true">
              <span className="text-white text-[9px] font-mono-axt">∞</span>
              <div className="flex-1 h-px bg-white" />
              <span className="text-white text-[9px] font-mono-axt tracking-[0.2em]">VITRUVIAN</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
